import React, { useMemo, useState } from "react";
import { CustomDropdown, DateField } from "../atoms";
import { useGetTaskAssigneesQuery, useGetTaskLabelsQuery } from "../../store";
import useAuth from "../../Hooks/useAuth";
import StatusBadge from "./atoms/StatusBadge";
import PriorityIcon from "./atoms/PriorityIcon";
import UserChip from "./atoms/UserChip";
import LabelChip from "./atoms/LabelChip";
import AssigneeChips from "./AssigneeChips";
import TaskConfirmModal, { OVER_DRAWER_Z } from "./TaskConfirmModal";
import { hasOpenSubtasks } from "./taskChecklistUtils";
import {
  STATUSES, PRIORITIES, TYPES, getType, colorClass, userName,
  toDateOnly, toDayString, formatDay, formatDateTime,
} from "./taskConstants";

// İş detayı sağ paneli. Her kayıt saveField({ alan }) ile (TaskDetailDrawer -> updateTask).
// can.edit yoksa tümü salt okunur; atanan ve düzenleyenler ayrıca can.assign ister.

const toOptions = (list) => list.map((x) => ({ id: x.id, label: x.label }));
const STATUS_OPTIONS = toOptions(STATUSES);
const PRIORITY_OPTIONS = toOptions(PRIORITIES);
const TYPE_OPTIONS = toOptions(TYPES);

function Field({ label, children }) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-b-0">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="text-sm text-gray-800 min-w-0">{children}</div>
    </div>
  );
}

const Muted = ({ children }) => <span className="text-sm text-gray-400">{children}</span>;

function EnumSelect({ options, value, onSave }) {
  return (
    <div data-testid="field-editor">
      <CustomDropdown
        compact
        showSearch={false}
        options={options}
        value={options.find((o) => o.id === value) || null}
        onChange={(o) => o && o.id !== value && onSave(o.id)}
      />
    </div>
  );
}

function DayField({ value, onSave }) {
  const current = toDateOnly(value);
  const currentStr = toDayString(current);
  return (
    <div data-testid="field-editor" className="flex items-center gap-2">
      <DateField
        size="sm"
        className="flex-1"
        format="dd.MM.yyyy"
        zIndex={OVER_DRAWER_Z}
        value={current}
        onChange={(d) => {
          if (d === null) return currentStr && onSave(null);
          const next = toDayString(d);
          if (next && next !== currentStr) onSave(next);
        }}
      />
      {current && (
        <button
          type="button"
          onClick={() => onSave(null)}
          className="text-xs text-gray-500 hover:text-fifth"
          aria-label="Tarihi temizle"
        >
          Temizle
        </button>
      )}
    </div>
  );
}

// Contract 1.8: atananlar tam liste; × / + farkı güncel kayda uygulanır (saveField sıralı).
function AssigneesField({ task, canAssign, onSave }) {
  const shown = task.assignees || [];
  return (
    <AssigneeChips
      users={shown}
      canEdit={canAssign}
      onChange={(next) => {
        const before = shown.map((u) => u._id);
        const added = next.filter((id) => !before.includes(id));
        const removed = before.filter((id) => !next.includes(id));
        return onSave((latest) => {
          const current = (latest.assignees || []).map((u) => u._id);
          const base = current.filter((id) => !removed.includes(id));
          const list = [...base, ...added.filter((id) => !base.includes(id))];
          const same = list.length === current.length && list.every((id) => current.includes(id));
          return same ? null : { assignees: list };
        });
      }}
    />
  );
}

function LabelsSelect({ task, onSave }) {
  const { auth } = useAuth();
  const { data } = useGetTaskLabelsQuery({ token: auth?.accessToken });
  const options = useMemo(() => {
    const labels = data?.data?.labels || [];
    const list = labels.map((l) => ({ id: l._id, label: l.name }));
    // Silinmiş/listede olmayan mevcut etiketler de seçili görünsün
    (task.labels || []).forEach((l) => {
      if (!list.some((o) => o.id === l._id)) list.push({ id: l._id, label: l.name });
    });
    return list;
  }, [data, task.labels]);
  const selectedIds = (task.labels || []).map((l) => l._id);
  return (
    <div data-testid="field-editor">
      <CustomDropdown
        compact
        multi
        placeholder="Etiket seç"
        options={options}
        value={options.filter((o) => selectedIds.includes(o.id))}
        onChange={(sel) => {
          // Gösterilen seçimle farkı (eklenen/çıkarılan) al; kaydı güncel etiketlere uygula.
          // Yanıt gelmeden art arda tıklamalar da birbirini ezmez (saveField sıralı).
          const next = sel.map((o) => o.id);
          const added = next.filter((id) => !selectedIds.includes(id));
          const removed = selectedIds.filter((id) => !next.includes(id));
          if (!added.length && !removed.length) return;
          onSave((latest) => {
            const base = (latest.labels || []).map((l) => l._id).filter((id) => !removed.includes(id));
            const labels = [...base, ...added.filter((id) => !base.includes(id))];
            const same =
              labels.length === (latest.labels || []).length &&
              labels.every((id) => (latest.labels || []).some((l) => l._id === id));
            return same ? null : { labels };
          });
        }}
      />
    </div>
  );
}

// Contract 1.4: düzenleyenler tam liste olarak kaydedilir; fark güncel kayda uygulanır.
function EditorsSelect({ task, onSave }) {
  const { auth } = useAuth();
  const [search, setSearch] = useState("");
  const { data, isFetching } = useGetTaskAssigneesQuery({ token: auth?.accessToken, search });
  const editors = task.editors || [];
  const options = useMemo(() => {
    const list = (data?.data?.users || []).map((u) => ({ id: u._id, label: userName(u) }));
    // Aramada görünmeyen mevcut düzenleyenler de seçili kalsın
    editors.forEach((e) => {
      if (!list.some((o) => o.id === e._id)) list.push({ id: e._id, label: userName(e) });
    });
    return list;
  }, [data, editors]);
  const selectedIds = editors.map((e) => e._id);
  return (
    <div data-testid="field-editor">
      <CustomDropdown
        compact
        multi
        placeholder="Kişi ekle"
        options={options}
        loading={isFetching}
        onSearch={setSearch}
        searchPlaceholder="Kişi ara..."
        value={options.filter((o) => selectedIds.includes(o.id))}
        onChange={(sel) => {
          const next = sel.map((o) => o.id);
          const added = next.filter((id) => !selectedIds.includes(id));
          const removed = selectedIds.filter((id) => !next.includes(id));
          if (!added.length && !removed.length) return;
          onSave((latest) => {
            const current = (latest.editors || []).map((e) => e._id);
            const base = current.filter((id) => !removed.includes(id));
            const list = [...base, ...added.filter((id) => !base.includes(id))];
            const same = list.length === current.length && list.every((id) => current.includes(id));
            return same ? null : { editors: list };
          });
        }}
      />
    </div>
  );
}

const refText = (ref) => {
  if (!ref) return null;
  return ref.name || `#${String(ref._id || "").slice(-6)}`;
};

export const OPEN_SUBTASKS_MESSAGE = "Açık alt işler var. Yine de tamamlansın mı?";

function TaskFieldsPanel({ task, saveField }) {
  const [confirmDone, setConfirmDone] = useState(false);
  const canEdit = Boolean(task?.can?.edit);
  // Contract 1.1: açık alt iş varken "Bitti" engellenmez; önce onay sorulur.
  const saveStatus = (status) => {
    if (status === "done" && hasOpenSubtasks(task)) {
      setConfirmDone(true);
      return;
    }
    saveField({ status });
  };
  const canAssign = canEdit && Boolean(task?.can?.assign);
  const type = getType(task.type);

  return (
    <div data-testid="task-fields" className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-2">
      <Field label="Durum">
        {canEdit ? (
          <EnumSelect options={STATUS_OPTIONS} value={task.status} onSave={saveStatus} />
        ) : (
          <StatusBadge status={task.status} />
        )}
      </Field>

      <Field label="Atanan">
        <AssigneesField task={task} canAssign={canAssign} onSave={saveField} />
      </Field>

      <Field label="Düzenleyenler">
        {canAssign && <EditorsSelect task={task} onSave={saveField} />}
        {(task.editors || []).length > 0 ? (
          <div className={`flex flex-col gap-1 ${canAssign ? "mt-2" : ""}`}>
            {task.editors.map((e) => (
              <UserChip key={e._id} user={e} size="sm" />
            ))}
          </div>
        ) : (
          !canAssign && <Muted>Düzenleyen yok</Muted>
        )}
      </Field>

      <Field label="Öncelik">
        {canEdit ? (
          <EnumSelect options={PRIORITY_OPTIONS} value={task.priority} onSave={(priority) => saveField({ priority })} />
        ) : (
          <PriorityIcon priority={task.priority} showLabel />
        )}
      </Field>

      <Field label="Tür">
        {canEdit ? (
          <EnumSelect options={TYPE_OPTIONS} value={task.type} onSave={(t) => saveField({ type: t })} />
        ) : (
          <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-Medium ${type?.className || ""}`}>
            {type?.label || task.type}
          </span>
        )}
      </Field>

      <Field label="Proje">
        {task.project ? (
          <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-Medium ${colorClass(task.project.color)}`}>
            {task.project.key} · {task.project.name}
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </Field>

      <Field label="Etiketler">
        {canEdit && <LabelsSelect task={task} onSave={saveField} />}
        {(task.labels || []).length > 0 ? (
          <div className={`flex flex-wrap gap-1 ${canEdit ? "mt-2" : ""}`}>
            {task.labels.map((l) => (
              <LabelChip key={l._id} label={l} />
            ))}
          </div>
        ) : (
          !canEdit && <Muted>Etiket yok</Muted>
        )}
      </Field>

      <Field label="Başlangıç">
        {canEdit ? (
          <DayField value={task.startDate} onSave={(startDate) => saveField({ startDate })} />
        ) : (
          formatDay(task.startDate) || <Muted>—</Muted>
        )}
      </Field>

      <Field label="Bitiş">
        {canEdit ? (
          <DayField value={task.dueDate} onSave={(dueDate) => saveField({ dueDate })} />
        ) : (
          formatDay(task.dueDate) || <Muted>—</Muted>
        )}
      </Field>

      {/* İlgili cihaz/firma: bu modülde cihaz/firma listesi yok; salt okunur gösterilir. */}
      <Field label="İlgili cihaz">{refText(task.relatedDevice) || <Muted>—</Muted>}</Field>
      <Field label="İlgili firma">{refText(task.relatedFirm) || <Muted>—</Muted>}</Field>

      <Field label="Takipçiler">
        {(task.watchers || []).length ? (
          <div className="flex flex-col gap-1">
            {task.watchers.map((w) => (
              <UserChip key={w._id} user={w} size="sm" />
            ))}
          </div>
        ) : (
          <Muted>Takipçi yok</Muted>
        )}
      </Field>

      <Field label="Açan">{task.reporter ? <UserChip user={task.reporter} size="sm" /> : <Muted>—</Muted>}</Field>

      <Field label="Oluşturma / Güncelleme">
        <p className="text-xs text-gray-600">{formatDateTime(task.createdAt)}</p>
        <p className="text-xs text-gray-600">{formatDateTime(task.updatedAt)}</p>
      </Field>

      <TaskConfirmModal
        open={confirmDone}
        title="Alt işler açık"
        message={OPEN_SUBTASKS_MESSAGE}
        confirmLabel="Tamamla"
        onConfirm={() => {
          setConfirmDone(false);
          saveField({ status: "done" });
        }}
        onClose={() => setConfirmDone(false)}
      />
    </div>
  );
}

export default TaskFieldsPanel;
